import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme,
  Grid,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import useOpenRequest from "../../hooks/useOpenRequest";
import { API_ENDPOINT } from "../../utils/constants";
import MuPb from "../widgets/MuPb";
import { LoadingButton } from "@mui/lab";
import MyDatePicker from "../formbuilder/datetime/MyDatePicker";
import dayjs from "dayjs";
import { toastError } from "../../utils/util";
import axios from "axios";
import { toast } from "react-toastify";

const VisitorRequestNew = () => {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [dateSelect, setDateSelect] = useState(null);
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");

  // Verify visitor profile token
  const {
    data: verifyData,
    isLoading: isVerifying,
    isError: isVerifyError,
    error: verifyError,
  } = useOpenRequest(
    `${API_ENDPOINT}user/verify-visitor-profile-token?id=${id}`
  );

  // Fetch visitor profile details upon successful verification
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isError: isProfileError,
    error: profileError,
  } = useQuery(
    ["visitorProfile", id],
    async () => {
      const response = await fetch(
        `${API_ENDPOINT}user/get-visitor-profile-request?id=${id}`
      );
      if (!response.ok) throw new Error("Failed to fetch profile details");
      return response.json();
    },
    { enabled: !!verifyData }
  );

  const handleStaffSelect = (event) => {
    setSelectedStaff(event.target.value);
  };

  const handleTimeChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleSubmit = async () => {
    try {
      if (!timeFrom || !timeTo) {
        toast.error("Please select both start and end times");
        return;
      }

      const targetPerson = profileData.staffList.find(
        (s) => s._id === selectedStaff
      );
      const response = await axios.post(
        `${API_ENDPOINT}user/visitor-meeting-request?id=${id}`,
        {
          targetPerson,
          meetingDate: dateSelect,
          timeFrom: timeFrom,
          timeTo: timeTo,
        }
      );
      toast.success(response.data.message);
      navigate(`/visitor-profile/${id}`);
    } catch (error) {
      toastError(error);
    }
  };

  if (isVerifying) return <MuPb />;
  if (isVerifyError)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 400 }}>
          {verifyError?.message}
        </Alert>
      </Box>
    );
  if (isLoadingProfile)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (isProfileError)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ width: "100%", maxWidth: 400 }}>
          {profileError?.message}
        </Alert>
      </Box>
    );

  return (
    <Box display="flex" justifyContent="center" my={4} px={2}>
      <Card
        sx={{
          maxWidth: 500,
          width: "100%",
          boxShadow: 3,
          mt: 2,
          borderRadius: 4,
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 150, // Fixed height for 3:1 banner
            mt: -1,
          }}
        >
          {profileData?.settings?.bnLogo && (
            <Box
              component="img"
              src={profileData.settings.bnLogo}
              alt={`${profileData?.company} banner`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px 8px 0 0",
                boxShadow: 2,
              }}
            />
          )}
          {profileData?.settings?.srLogo && (
            <Avatar
              src={profileData.settings.srLogo}
              alt={`${profileData?.company} logo`}
              sx={{
                width: 120,
                height: 120,
                position: "absolute",
                bottom: -60, // Half of the avatar hangs below the banner
                left: "50%",
                transform: "translateX(-50%)",
                border: "4px solid white",
                bgcolor: "grey.100",
                boxShadow: 3,
              }}
            />
          )}
        </Box>
        <CardContent sx={{ position: "relative" }}>
          <Typography mt={10} variant="h6" gutterBottom sx={{ mb: 2 }}>
            Requesting to meet at{" "}
            <span style={{ color: theme.palette.primary.main }}>
              {profileData.company}
            </span>
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="staff-select-label">Select staff member</InputLabel>
            <Select
              labelId="staff-select-label"
              id="staff-select"
              value={selectedStaff}
              label="Select staff member"
              onChange={handleStaffSelect}
              renderValue={(selected) => {
                const staff = profileData.staffList.find(
                  (s) => s._id === selected
                );
                return staff ? (
                  <Stack direction={"row"} gap={1}>
                    <Avatar src={staff.profileImage} alt={staff.name} />
                    <Typography sx={{ alignSelf: "center" }} variant="h6">
                      {staff.name}
                    </Typography>
                  </Stack>
                ) : (
                  "Select a staff member"
                );
              }}
            >
              {profileData?.staffList?.map((staff) => (
                <MenuItem key={staff._id} value={staff._id}>
                  <ListItemAvatar>
                    <Avatar src={staff.profileImage} alt={staff.name} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={staff.name}
                    secondary={staff.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MyDatePicker
            value={dateSelect}
            onChange={(newV) => {
              setDateSelect(newV);
            }}
            label="Select the Date"
            size="medium"
          />

          <Stack sx={{ my: 2 }} direction={"row"} gap={1}>
            <TextField
              label="Start Time"
              type="time"
              value={timeFrom}
              onChange={handleTimeChange(setTimeFrom)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300, // 5 min steps
              }}
              fullWidth
            />
            <TextField
              label="End Time"
              type="time"
              value={timeTo}
              onChange={handleTimeChange(setTimeTo)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300, // 5 min steps
              }}
              fullWidth
            />
          </Stack>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <LoadingButton
              variant="contained"
              size="large"
              disabled={!selectedStaff || !timeFrom || !timeTo || !dateSelect}
              onClick={handleSubmit}
              sx={{ px: 4 }}
            >
              Confirm Meeting Request
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisitorRequestNew;
